import { useEffect, useState } from 'react';
import { SubScreen } from '../../components/layout/Screens';
import {
  fetchLegalConfig,
  lireLeConsentement,
  type ConfigLegale,
  type Consentement,
} from '../../firebase/legal';
import { useSession } from '../../store/SessionContext';
import { LienLegal } from './LienLegal';
import { t } from '../../i18n';

/**
 * Les trois documents, et la preuve de ce qu'on a accepte.
 *
 * # Pourquoi un ecran, et pas trois lignes dans les reglages
 *
 * Les reglages sont une liste qu'on parcourt des yeux pour trouver une
 * chose precise. Y poser trois documents juridiques, chacun avec sa
 * version, allongeait cette liste sans que personne ne les cherche
 * quotidiennement. Une ligne, et ce qu'elle annonce derriere.
 *
 * # La version acceptee
 *
 * Elle n'est pas decorative. Le RGPD demande de pouvoir montrer a quoi
 * et quand une personne a consenti ; c'est aussi ce qui permet a un
 * membre de le verifier lui-meme, sans avoir a le demander.
 *
 * # Les adresses de repli
 *
 * Si la configuration ne repond pas, les liens pointent quand meme vers
 * le domaine, qui ne bouge pas. Un document legal doit rester
 * consultable meme quand le reste echoue.
 */
export function LegalScreen() {
  const { user } = useSession();
  const [config, setConfig] = useState<ConfigLegale | null>(null);
  const [accepte, setAccepte] = useState<Consentement | null>(null);

  useEffect(() => {
    void fetchLegalConfig().then(setConfig);
    if (user?.id) void lireLeConsentement(user.id).then(setAccepte);
  }, [user?.id]);

  const documents = [
    {
      cle: 'cgu',
      titre: t.legal.documents.cgu,
      resume: t.legal.ecran.resumeCgu,
      url: config?.cgu.url ?? 'https://siroccoapp.fr/cgu',
      version: config?.cgu.version,
    },
    {
      cle: 'privacy',
      titre: t.legal.documents.confidentialite,
      resume: t.legal.ecran.resumeConfidentialite,
      url: config?.privacy.url ?? 'https://siroccoapp.fr/confidentialite',
      version: config?.privacy.version,
    },
    {
      cle: 'legalNotice',
      titre: t.legal.documents.mentions,
      resume: t.legal.ecran.resumeMentions,
      url: config?.legalNotice.url ?? 'https://siroccoapp.fr/mentions-legales',
      version: config?.legalNotice.version,
    },
  ];

  return (
    <SubScreen title={t.legal.ecran.titre}>
      <div className="legal-docs">
        {documents.map((doc) => (
          <LienLegal key={doc.cle} url={doc.url} className="legal-docs__item">
            <span className="legal-docs__titre">{doc.titre}</span>
            <span className="legal-docs__resume">{doc.resume}</span>
            {doc.version && (
              <span className="legal-docs__version">{t.legal.documents.version(doc.version)}</span>
            )}
          </LienLegal>
        ))}
      </div>

      {accepte && (
        <p className="legal-docs__accepte">
          {t.legal.ecran.accepte(
            accepte.cguVersion,
            accepte.privacyVersion,
            accepte.acceptedAt ?? null,
          )}
        </p>
      )}
    </SubScreen>
  );
}
